import { CanvasTexture, SRGBColorSpace, type Texture } from 'three';

export interface KokoFrames {
  dance: Texture[]; // 8 кадров танца
  jump: Texture; // кадр прыжка
}

/**
 * Маскот-диджей — петроглиф Кокопелли (порт 1:1 из концепта). Рисуется на
 * canvas 256×256, биллборд. `dance` — 8 фаз танца (флейта + машущая рука +
 * пляшущие ноги), `jump` — руки вверх во время прыжка по трекам.
 */
function drawKoko(phase: number, mode: 'dance' | 'jump', maxAniso: number): Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const x = c.getContext('2d')!;
  const ink = '#2A2A2D';
  const acc = '#685AE9';
  x.lineCap = 'round';
  x.lineJoin = 'round';
  const hipX = 120;
  const hipY = 150;
  const neckX = 122;
  const neckY = 92;
  const headX = 130;
  const headY = 70;
  const headR = 18;
  const sA = Math.sin(phase);

  // покачивание всего тела вокруг бёдер
  const tilt = mode === 'jump' ? 0 : sA * 0.15;
  x.translate(hipX, hipY);
  x.rotate(tilt);
  x.translate(-hipX, -hipY);

  // горб (заплечный короб)
  x.fillStyle = ink;
  x.beginPath();
  x.moveTo(neckX - 1, neckY);
  x.quadraticCurveTo(84, 116, hipX - 2, hipY);
  x.quadraticCurveTo(120, 120, neckX - 1, neckY);
  x.fill();
  // торс
  x.strokeStyle = ink;
  x.lineWidth = 16;
  x.beginPath();
  x.moveTo(neckX, neckY);
  x.quadraticCurveTo(104, 120, hipX, hipY);
  x.stroke();
  // голова
  x.beginPath();
  x.arc(headX, headY, headR, 0, 7);
  x.fill();
  // антенны-волосы
  x.lineWidth = 6.5;
  for (const a of [-2.0, -1.62, -1.24]) {
    x.beginPath();
    x.moveTo(headX + Math.cos(a) * headR, headY + Math.sin(a) * headR);
    x.lineTo(headX + Math.cos(a) * (headR + 26), headY + Math.sin(a) * (headR + 26));
    x.stroke();
  }

  if (mode === 'jump') {
    // руки вверх, ноги поджаты
    x.lineWidth = 10;
    x.strokeStyle = ink;
    x.beginPath();
    x.moveTo(neckX + 2, neckY + 10);
    x.lineTo(156, 76);
    x.lineTo(178, 56);
    x.stroke();
    x.beginPath();
    x.moveTo(neckX - 4, neckY + 10);
    x.lineTo(92, 74);
    x.lineTo(72, 54);
    x.stroke();
    x.strokeStyle = acc;
    x.lineWidth = 8.5;
    x.beginPath();
    x.moveTo(142, 86);
    x.lineTo(200, 80);
    x.stroke();
    x.fillStyle = acc;
    x.beginPath();
    x.arc(200, 80, 5.5, 0, 7);
    x.fill();
    x.strokeStyle = ink;
    x.lineWidth = 13;
    x.beginPath();
    x.moveTo(hipX, hipY);
    x.lineTo(hipX + 20, hipY + 12);
    x.lineTo(hipX + 38, hipY - 4);
    x.stroke();
    x.beginPath();
    x.moveTo(hipX, hipY);
    x.lineTo(hipX - 16, hipY + 14);
    x.lineTo(hipX - 32, hipY - 2);
    x.stroke();
  } else {
    // рука с флейтой + покачивающаяся флейта
    x.lineWidth = 10;
    x.strokeStyle = ink;
    x.beginPath();
    x.moveTo(neckX + 2, neckY + 12);
    x.lineTo(150, 106);
    x.lineTo(168, 98);
    x.stroke();
    x.strokeStyle = acc;
    x.lineWidth = 8.5;
    x.beginPath();
    x.moveTo(142, 86);
    x.lineTo(202, 80 + sA * 4);
    x.stroke();
    x.fillStyle = acc;
    x.beginPath();
    x.arc(202, 80 + sA * 4, 5.5, 0, 7);
    x.fill();
    // свободная рука над головой, машет в такт
    const hx = 92 + sA * 18;
    const hy = 48 - Math.abs(sA) * 16;
    x.strokeStyle = ink;
    x.lineWidth = 10;
    x.beginPath();
    x.moveTo(neckX - 4, neckY + 8);
    x.lineTo(102, 64);
    x.lineTo(hx, hy);
    x.stroke();
    // пляшущие ноги — попеременно
    x.lineWidth = 13;
    if (sA > 0) {
      x.beginPath();
      x.moveTo(hipX, hipY);
      x.lineTo(hipX + 13, hipY + 20 - sA * 18);
      x.lineTo(hipX + 30 + sA * 10, hipY + 46 - sA * 30);
      x.stroke();
    } else {
      x.beginPath();
      x.moveTo(hipX, hipY);
      x.lineTo(hipX + 9, hipY + 36);
      x.lineTo(hipX + 20, hipY + 70);
      x.stroke();
    }
    if (sA < 0) {
      x.beginPath();
      x.moveTo(hipX, hipY);
      x.lineTo(hipX - 13, hipY + 20 + sA * 18);
      x.lineTo(hipX - 30 + sA * 10, hipY + 46 + sA * 30);
      x.stroke();
    } else {
      x.beginPath();
      x.moveTo(hipX, hipY);
      x.lineTo(hipX - 9, hipY + 36);
      x.lineTo(hipX - 20, hipY + 68);
      x.stroke();
    }
  }

  const tex = new CanvasTexture(c);
  tex.colorSpace = SRGBColorSpace;
  tex.anisotropy = maxAniso || 4;
  return tex;
}

export function makeKokopelli(maxAniso: number): KokoFrames {
  const dance: Texture[] = [];
  for (let k = 0; k < 8; k++) dance.push(drawKoko((k / 8) * Math.PI * 2, 'dance', maxAniso));
  return { dance, jump: drawKoko(0, 'jump', maxAniso) };
}
